import { useState } from 'react'
import { RecognitionCard } from './RecognitionCard'
import { StarIcon } from './icons'
import { useHubLang } from '../lib/i18n'
import { FEED_STARS } from '../lib/hubCopy'
import { loadRecognitionGroup, type RecognitionGroup, type WallItem } from '../lib/stars'

/**
 * RECOGNITIONS IN THE FEED — 333 (30 Oct 2026).
 *
 * A platform-support idea from 29 Oct: "unless I intentionally click to go into
 * Star One, I don't see all the cool things people are saying about each
 * other." David chose the shape: ONE card per gap between two posts, holding
 * the recognitions given in that gap — the newest few, and Show all opens the
 * rest right here. Measured on prod that day: 34 recognitions against 18 posts
 * in a week, so a card each would have made the feed two thirds recognitions,
 * which is the reason they were kept out on 20 Sep. Grouping is what answers
 * that reason; do not unroll the group into one card per recognition.
 *
 * ── THE CARD IS THE WALL'S CARD ─────────────────────────────────────────────
 *
 * Each row is RecognitionCard, unchanged: reactions, the author's edit window
 * and Take down all work here as on #/stars. A second rendering of a
 * recognition would be a second answer to what one looks like.
 *
 * ── NO RANKING ──────────────────────────────────────────────────────────────
 *
 * Newest first, as the database returned them (the wall's order). Never sorted
 * by stars and never a "top recognitions" anything (D10, B22).
 *
 * Takes its group as a prop so the suite can render it (an effect does not run
 * under renderToString); `loadAll` is a prop for the same reason.
 */
export function FeedRecognitions({
  group,
  onChanged,
  loadAll = loadRecognitionGroup,
}: {
  group: RecognitionGroup
  onChanged?: () => void | Promise<void>
  loadAll?: (g: RecognitionGroup) => Promise<WallItem[]>
}) {
  const t = FEED_STARS[useHubLang()]
  const [all, setAll] = useState<WallItem[] | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle')

  const shown = all ?? group.items
  const more = group.total > group.items.length

  async function showAll() {
    setState('loading')
    try {
      setAll(await loadAll(group))
      setState('idle')
    } catch {
      setState('failed')
    }
  }

  return (
    <section className="post feedstars" aria-label={t.label}>
      <header className="feedstars__head">
        <span className="feedstars__mark" aria-hidden="true">
          <StarIcon />
        </span>
        <p className="feedstars__title">
          <strong>Star One</strong>
          <span className="feedstars__count"> · {t.count(group.total, group.capped)}</span>
        </p>
        <a className="feedstars__wall" href="#/stars">{t.openWall}</a>
      </header>
      <ul className="stars__list">
        {shown.map((item) => (
          <RecognitionCard key={item.contentId} item={item} onChanged={onChanged} />
        ))}
      </ul>
      {more && (
        <div className="feedstars__more">
          {all === null ? (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => void showAll()} disabled={state === 'loading'}>
              {state === 'loading' ? t.loading : t.showAll(group.total, group.capped)}
            </button>
          ) : (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setAll(null)}>
              {t.showFewer}
            </button>
          )}
          {state === 'failed' && <p className="feedstars__err" role="status">{t.couldNotLoad}</p>}
        </div>
      )}
    </section>
  )
}
